/**
 * Image Cache Service
 * Caches blob URLs in memory to avoid re-fetching images when navigating
 */

interface CacheEntry {
  blobUrl: string;
  timestamp: number;
}

class ImageCache {
  private cache: Map<string, CacheEntry> = new Map();
  private readonly MAX_AGE = 30 * 60 * 1000; // 30 minutes

  /**
   * Get cached image URL
   */
  get(key: string): string | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if cache entry is still valid
    const age = Date.now() - entry.timestamp;
    if (age > this.MAX_AGE) {
      // Cache expired, revoke blob URL and remove entry
      URL.revokeObjectURL(entry.blobUrl);
      this.cache.delete(key);
      return null;
    }

    return entry.blobUrl;
  }

  /**
   * Set cached image URL
   */
  set(key: string, blobUrl: string): void {
    // If key already exists, revoke the old blob URL
    const existing = this.cache.get(key);
    if (existing) {
      URL.revokeObjectURL(existing.blobUrl);
    }

    this.cache.set(key, {
      blobUrl,
      timestamp: Date.now()
    });
  }

  /**
   * Clear a specific cache entry
   */
  delete(key: string): void {
    const entry = this.cache.get(key);
    if (entry) {
      URL.revokeObjectURL(entry.blobUrl);
      this.cache.delete(key);
    }
  }

  /**
   * Clear all cached images
   */
  clear(): void {
    // Revoke all blob URLs before clearing
    for (const entry of this.cache.values()) {
      URL.revokeObjectURL(entry.blobUrl);
    }
    this.cache.clear();
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      const age = now - entry.timestamp;
      if (age > this.MAX_AGE) {
        URL.revokeObjectURL(entry.blobUrl);
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }
}

// Singleton instance
const imageCache = new ImageCache();

// Cleanup expired entries every 5 minutes
setInterval(() => {
  imageCache.cleanup();
}, 5 * 60 * 1000);

export default imageCache;
